import { useRef, useState, useCallback } from 'react';
import ClockIcon from './icons/ClockIcon';
import RewardProgressBar from '../../../components/common/RewardProgressBar';

const BannerStatsSlider = ({
    language,
    levelId,
    activeTab,
    statsDetail,
    isBannerLight,
    bannerTextValueSpec,
    bannerTextLabelSpec,
    bannerIconSpec,
    onBadgeClick,
}) => {
    const sliderRef = useRef(null);
    const [activeSlide, setActiveSlide] = useState(0);
    const totalSlides = 4;

    const handleSliderScroll = useCallback(() => {
        if (sliderRef.current) {
            const scrollLeft = sliderRef.current.scrollLeft;
            const slideWidth = sliderRef.current.offsetWidth;
            const newIndex = Math.round(scrollLeft / slideWidth);
            setActiveSlide(Math.min(newIndex, totalSlides - 1));
        }
    }, []);

    return (
        <div className="mt-8 -mx-6 md:mx-0">
            <div
                data-help="banner-stats"
                ref={sliderRef}
                onScroll={handleSliderScroll}
                className="flex md:grid md:grid-cols-2 xl:grid-cols-4 md:gap-4 md:px-0 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none no-scrollbar"
            >
                {/* Days Remaining */}
                <div className="w-full md:w-auto flex-shrink-0 md:flex-shrink snap-center px-6 md:px-0">
                    <div onClick={() => onBadgeClick('days')} className="bg-black/40 backdrop-blur-md rounded-2xl p-4 border border-white/10 flex flex-col justify-between group hover:bg-black/50 hover:scale-[1.02] transition-all cursor-pointer h-full">
                        <div className="flex items-center justify-between mb-2">
                            <p className={`${bannerTextLabelSpec} text-[10px] font-black uppercase tracking-widest`}>{language === 'fr' ? 'Jours restants' : 'Days remaining'}</p>
                            <div className="p-1 bg-white/10 rounded-lg">
                                <ClockIcon className={`w-3.5 h-3.5 ${bannerIconSpec}`} />
                            </div>
                        </div>
                        {levelId === 'pstar' ? (
                            <p className={`text-3xl font-black ${bannerTextValueSpec} leading-none`}>∞</p>
                        ) : statsDetail?.hasCourseAvailable ? (
                            <div className="flex gap-3">
                                <div className="flex flex-col">
                                    <span className={`text-[9px] font-bold uppercase tracking-wider ${isBannerLight ? 'text-black/50' : 'text-white/65'}`}>
                                        Exam
                                    </span>
                                    <span className={`text-2xl font-black ${bannerTextValueSpec}`}>{statsDetail.daysRemainingExam ?? 0}</span>
                                </div>
                                <div className={`w-px h-8 ${isBannerLight ? 'bg-black/15' : 'bg-white/15'}`} />
                                <div className="flex flex-col">
                                    <span className={`text-[9px] font-bold uppercase tracking-wider ${isBannerLight ? 'text-black/50' : 'text-white/65'}`}>
                                        {language === 'fr' ? 'Cours' : 'Course'}
                                    </span>
                                    <span className={`text-2xl font-black ${bannerTextValueSpec}`}>{statsDetail.daysRemainingCourse ?? 0}</span>
                                </div>
                            </div>
                        ) : (
                            <p className={`text-3xl font-black ${bannerTextValueSpec} leading-none`}>
                                {statsDetail.daysRemaining}
                            </p>
                        )}
                    </div>
                </div>

                {/* Progression (contextual) */}
                <div className="w-full md:w-auto flex-shrink-0 md:flex-shrink snap-center px-6 md:px-0">
                    <div onClick={() => onBadgeClick('progression')} className="bg-black/40 backdrop-blur-md rounded-2xl p-4 border border-white/10 flex flex-col justify-between group hover:bg-black/50 hover:scale-[1.02] transition-all cursor-pointer h-full">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <p className={`${bannerTextLabelSpec} text-[10px] font-black uppercase tracking-widest`}>Progression</p>
                                <span className={`text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${isBannerLight ? 'bg-black/10 text-black/50' : 'bg-white/15 text-white/65'}`}>
                                    {activeTab === 'courses' ? (language === 'fr' ? 'Cours' : 'Courses') : 'Exams'}
                                </span>
                            </div>
                            <div className="p-1 bg-white/10 rounded-lg">
                                <svg className={`w-3.5 h-3.5 ${bannerIconSpec}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                </svg>
                            </div>
                        </div>
                        {activeTab === 'courses' ? (
                            <div className="flex items-baseline gap-1">
                                <span className={`text-3xl font-black ${bannerTextValueSpec}`}>{statsDetail?.progressCourse ?? 0}</span>
                                <span className={`text-sm font-bold ${isBannerLight ? 'text-black/40' : 'text-white/55'}`}>%</span>
                            </div>
                        ) : (
                            <div className="flex items-baseline gap-1">
                                <span className={`text-3xl font-black ${bannerTextValueSpec}`}>{statsDetail.progressExamen}</span>
                                <span className={`text-sm font-bold ${isBannerLight ? 'text-black/40' : 'text-white/55'}`}>%</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Maîtrise (contextual) */}
                <div className="w-full md:w-auto flex-shrink-0 md:flex-shrink snap-center px-6 md:px-0">
                    <div onClick={() => onBadgeClick('comprehension')} className="bg-black/40 backdrop-blur-md rounded-2xl p-4 border border-white/10 flex flex-col justify-between group hover:bg-black/50 hover:scale-[1.02] transition-all cursor-pointer h-full">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <p className={`${bannerTextLabelSpec} text-[10px] font-black uppercase tracking-widest`}>{language === 'fr' ? 'Maîtrise' : 'Mastery'}</p>
                                <span className={`text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${isBannerLight ? 'bg-black/10 text-black/50' : 'bg-white/15 text-white/65'}`}>
                                    {activeTab === 'courses' ? (language === 'fr' ? 'Cours' : 'Courses') : 'Exams'}
                                </span>
                            </div>
                            <div className="p-1 bg-white/10 rounded-lg">
                                <svg className={`w-3.5 h-3.5 ${bannerIconSpec}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                </svg>
                            </div>
                        </div>
                        {activeTab === 'courses' ? (
                            (statsDetail?.courseQuizCount ?? 0) > 0 ? (
                                <div className="flex gap-3">
                                    <div className="flex flex-col">
                                        <span className={`text-[9px] font-bold uppercase tracking-wider ${isBannerLight ? 'text-black/50' : 'text-white/65'}`}>
                                            {language === 'fr' ? 'Moyenne' : 'Average'}
                                        </span>
                                        <div className="flex items-baseline gap-0.5">
                                            <span className={`text-2xl font-black ${bannerTextValueSpec}`}>{statsDetail.comprehensionCourse ?? 0}</span>
                                            <span className={`text-xs font-bold ${isBannerLight ? 'text-black/40' : 'text-white/55'}`}>%</span>
                                        </div>
                                    </div>
                                    <div className={`w-px h-8 self-center ${isBannerLight ? 'bg-black/15' : 'bg-white/15'}`} />
                                    <div className="flex flex-col">
                                        <span className={`text-[9px] font-bold uppercase tracking-wider ${isBannerLight ? 'text-black/50' : 'text-white/65'}`}>
                                            {language === 'fr' ? 'Dernière' : 'Last'}
                                        </span>
                                        <div className="flex items-baseline gap-0.5">
                                            <span className={`text-2xl font-black ${bannerTextValueSpec}`}>
                                                {(statsDetail.courseQuizLastSessionTotal ?? 0) > 0 ? statsDetail.courseQuizLastSessionRate : '—'}
                                            </span>
                                            {(statsDetail.courseQuizLastSessionTotal ?? 0) > 0 && (
                                                <span className={`text-xs font-bold ${isBannerLight ? 'text-black/40' : 'text-white/55'}`}>%</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <span className={`text-sm font-bold ${isBannerLight ? 'text-black/40' : 'text-white/55'}`}>—</span>
                            )
                        ) : (
                            <div className="flex gap-3">
                                <div className="flex flex-col">
                                    <span className={`text-[9px] font-bold uppercase tracking-wider ${isBannerLight ? 'text-black/50' : 'text-white/65'}`}>
                                        {language === 'fr' ? 'Compréhension' : 'Comprehension'}
                                    </span>
                                    <div className="flex items-baseline gap-0.5">
                                        <span className={`text-2xl font-black ${bannerTextValueSpec}`}>
                                            {(statsDetail.comprehensionExamen ?? 0) === 0 && (statsDetail.correctCount ?? 0) > 0 ? '< 1' : (statsDetail.comprehensionExamen ?? 0)}
                                        </span>
                                        <span className={`text-xs font-bold ${isBannerLight ? 'text-black/40' : 'text-white/55'}`}>%</span>
                                    </div>
                                </div>
                                <div className={`w-px h-8 self-center ${isBannerLight ? 'bg-black/15' : 'bg-white/15'}`} />
                                <div className="flex flex-col">
                                    <span className={`text-[9px] font-bold uppercase tracking-wider ${isBannerLight ? 'text-black/50' : 'text-white/65'}`}>
                                        {language === 'fr' ? 'Réussite' : 'Success'}
                                    </span>
                                    <div className="flex items-baseline gap-0.5">
                                        <span className={`text-2xl font-black ${bannerTextValueSpec}`}>{statsDetail.successRate ?? 0}</span>
                                        <span className={`text-xs font-bold ${isBannerLight ? 'text-black/40' : 'text-white/55'}`}>%</span>
                                    </div>
                                </div>
                                <div className={`w-px h-8 self-center ${isBannerLight ? 'bg-black/15' : 'bg-white/15'}`} />
                                <div className="flex flex-col">
                                    <span className={`text-[9px] font-bold uppercase tracking-wider ${isBannerLight ? 'text-black/50' : 'text-white/65'}`}>
                                        {language === 'fr' ? 'Dernière' : 'Last'}
                                    </span>
                                    <div className="flex items-baseline gap-0.5">
                                        <span className={`text-2xl font-black ${bannerTextValueSpec}`}>
                                            {(statsDetail.lastSessionTotal ?? 0) > 0 ? statsDetail.lastSessionRate : '—'}
                                        </span>
                                        {(statsDetail.lastSessionTotal ?? 0) > 0 && (
                                            <span className={`text-xs font-bold ${isBannerLight ? 'text-black/40' : 'text-white/55'}`}>%</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Rewards */}
                <div className="w-full md:w-auto flex-shrink-0 md:flex-shrink snap-center px-6 md:px-0">
                    <div onClick={() => onBadgeClick('rewards')} className="bg-black/40 backdrop-blur-md rounded-2xl p-4 border border-white/10 flex flex-col justify-between group hover:bg-black/50 hover:scale-[1.02] transition-all cursor-pointer h-full">
                        <div className="flex items-center justify-between mb-2">
                            <p className={`${bannerTextLabelSpec} text-[10px] font-black uppercase tracking-widest`}>{language === 'fr' ? 'Récompenses' : 'Rewards'}</p>
                            <div className="p-1 bg-white/10 rounded-lg">
                                <img src="/img/application/Victoire.png" alt="" className="w-3.5 h-3.5 object-contain brightness-0 invert" />
                            </div>
                        </div>
                        <div className="flex items-baseline gap-1">
                            <span className={`text-3xl font-black ${bannerTextValueSpec}`}>{statsDetail.rewards}</span>
                            <span className={`text-lg font-bold ${isBannerLight ? 'text-black/40' : 'text-white/55'}`}>/{statsDetail.rewardsTotal ?? 10}</span>
                        </div>
                        {statsDetail?.rewardTiers?.length > 0 && (
                            <RewardProgressBar
                                count={statsDetail.rewards}
                                total={statsDetail.rewardsTotal ?? 10}
                                rewardTiers={statsDetail.rewardTiers}
                                color="rgba(255,255,255,0.6)"
                                size="sm"
                                variant="banner"
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Dot indicators - mobile only */}
            <div className="flex justify-center gap-1.5 mt-4 md:hidden">
                {[0, 1, 2, 3].map((index) => (
                    <button
                        key={index}
                        onClick={() => {
                            if (sliderRef.current) {
                                sliderRef.current.scrollTo({
                                    left: index * sliderRef.current.offsetWidth,
                                    behavior: 'smooth'
                                });
                            }
                        }}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                            activeSlide === index
                                ? `w-6 ${isBannerLight ? 'bg-black/60' : 'bg-white/80'}`
                                : `w-1.5 ${isBannerLight ? 'bg-black/20' : 'bg-white/30'}`
                        }`}
                    />
                ))}
            </div>
        </div>
    );
};

export default BannerStatsSlider;
